/**
 * Funding check for an ad-budget top-up. The money moves MAIN wallet → the brand's
 * acquisition budget (the backend performs the transfer when we POST the raised
 * `acquisition_budget`), so the main wallet has to actually cover the requested amount.
 *
 * Pure and split out of the component so the guard is unit-tested without a render.
 *
 * **A balance we cannot confirm is never treated as "enough".** The first version of
 * this guard only blocked on a strictly positive balance (`balance > 0 && amount >
 * balance`), deliberately deferring to the backend otherwise, so an owner whose main
 * wallet read 0 (empty, unreadable, or still loading) could top up any amount and the
 * backend did not stop it. See `topup-funding.test.ts`.
 */

/** What the main wallet can tell us about funding a requested top-up. */
export type TopupFundingStatus =
  /** No funding problem to report: the wallet covers the amount (or none is requested yet). */
  | 'covered'
  /** The balance is known and does NOT cover the amount. An empty wallet lands here. */
  | 'short'
  /** The wallets request hasn't settled yet, so the balance isn't known. */
  | 'loading'
  /** The wallets request failed, so there is no trustworthy balance to gate against. */
  | 'unknown';

export type TopupFunding = {
  status: TopupFundingStatus;
  /** True only for a positive amount the wallet is KNOWN to cover. Gates the confirm. */
  canFund: boolean;
  /** How much the main wallet is short, in major units. 0 unless `status` is `short`. */
  shortfall: number;
};

export type TopupFundingInput = {
  /** Requested top-up, major units. */
  amount: number;
  /** Main-wallet balance in major units, or `null` when it isn't known. */
  balance: number | null;
  /** The wallets request has settled (fulfilled OR rejected). */
  walletLoaded: boolean;
  /** The wallets request failed, so `balance` is a stale/initial value, not a real one. */
  walletError?: boolean;
  /**
   * `balance` and `amount` are in the SAME currency, so comparing them means something.
   * False when the owner has no MAIN wallet in the currency this top-up is priced in and
   * the resolver fell back to another one: the two figures are then not comparable at all,
   * and treating the larger number as the bigger sum would gate money on an exchange rate
   * nobody applied. Defaults to true so an existing caller can't silently opt out.
   */
  comparable?: boolean;
};

/** Compare in whole cents so float drift can't make a covered amount read as short. */
const cents = (value: number): number => Math.round(value * 100);

export function assessTopupFunding({
  amount,
  balance,
  walletLoaded,
  walletError = false,
  comparable = true,
}: TopupFundingInput): TopupFunding {
  const requested = Number.isFinite(amount) ? amount : 0;

  // Nothing requested yet (empty or cleared field): there is no funding problem to
  // report, so the tab stays quiet instead of flashing a wallet warning.
  if (requested <= 0) return { status: 'covered', canFund: false, shortfall: 0 };

  if (!walletLoaded) return { status: 'loading', canFund: false, shortfall: 0 };
  if (walletError || balance === null || !Number.isFinite(balance)) {
    return { status: 'unknown', canFund: false, shortfall: 0 };
  }
  // A balance in another currency is not a smaller or larger balance, it is an unknown one.
  // Reporting a shortfall here would quote the difference between two currencies as if it
  // were money owed.
  if (!comparable) return { status: 'unknown', canFund: false, shortfall: 0 };

  // A negative balance is an overdrawn wallet: it funds nothing.
  const available = Math.max(0, balance);
  if (cents(requested) > cents(available)) {
    return { status: 'short', canFund: false, shortfall: (cents(requested) - cents(available)) / 100 };
  }

  return { status: 'covered', canFund: true, shortfall: 0 };
}
